import { FinancialProductStrategy } from '@generated/graphql';
import type { FinancialProduct } from '@interfaces/financialProduct';
import type {
  AvailableSecurity,
  UnrecognizedSecurity,
} from '@ovComponents/3-pattern/securitiesReviewDialog/securitiesReviewDialog';
import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import { getHouseholdModelPortfolioSecurities } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.allocation.utils';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { appendSecurityRows } from '@utils/appendSecurityRows';
import { useToast } from 'hephaestus/web';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { EditedHouseholdModelPortfolio } from '../householdModelPortfolioDraft/householdAllocations';
import { normalizeCashSecurity } from '../utils/normalizeCashSecurity';
import {
  compactTaxRanksAfterRemoval,
  getEffectiveTaxRankAtIndex,
  getTaxRankValidation,
  useAssetClassGrouping,
} from '../utils/useAssetClassGrouping';
import { useCsvSecuritiesImport } from '../utils/useCsvSecuritiesImport';
import { useModelPortfolioCash } from '../utils/useModelPortfolioCash';
import { useInitializeLiveSecurities } from './initializeLiveSecuritiesContext';

const TEMP_ID_PREFIX = 'temp-';
/** Long enough to swallow typing, short enough to land between edits. */
const PERSIST_DEBOUNCE_MS = 400;

export type UseInitializeAllocationsEditorArgs = {
  draftModel: EditedHouseholdModelPortfolio;
};

/**
 * Note there are no validation flags here. What the advisor is held to is
 * decided in one place, `initializeAllocationsProblem`, which reads the draft
 * out of the workflow context — so the step's Alert and its commit guard cannot
 * disagree about whether the allocations are good.
 */
export type UseInitializeAllocationsEditorResult = {
  securities: Security[];
  securitiesWithRanks: Security[];
  isAssetClassStrategy: boolean;
  assetClassLevel: EditedHouseholdModelPortfolio['assetClassLevel'];
  groupedSecurities: ReturnType<
    typeof useAssetClassGrouping
  >['groupedSecurities'];
  unassignedSecurities: Security[];
  /** Writes any debounced row changes to the workflow context immediately. */
  flushPendingSecurities: () => void;
  onAddSecurities: (securities: FinancialProduct[]) => void;
  onAddCash?: () => void;
  onUpdateSecurity: (index: number, security: Security) => void;
  onRemoveSecurity: (index: number) => void;
  onUpdateTaxRank: (index: number, value: number) => void;
  onUpdateTargetPercentage: (index: number, value: number) => void;
  onReorderSecurities: (securities: Security[]) => void;
  importLoading: boolean;
  showReviewDialog: boolean;
  unrecognizedSecurities: UnrecognizedSecurity[];
  availableSecurities: AvailableSecurity[];
  handleImportFromCsv: (
    file: File,
    onProgress?: (progress: number, message?: string) => void,
  ) => Promise<void>;
  handleReviewSubmit: (replacements: Record<string, string>) => Promise<void>;
  handleReviewClose: () => void;
  cashLoading: boolean;
};

export const useInitializeAllocationsEditor = ({
  draftModel,
}: UseInitializeAllocationsEditorArgs): UseInitializeAllocationsEditorResult => {
  const { setWorkflowData } = useContext(WorkflowContext);
  const liveSecuritiesContext = useInitializeLiveSecurities();
  const { enqueueToast } = useToast();
  const { t } = useTranslation(['buildModels']);

  const [securities, setSecurities] = useState<Security[]>(
    () =>
      draftModel.securities ?? getHouseholdModelPortfolioSecurities(draftModel),
  );
  const securitiesRef = useRef(securities);
  securitiesRef.current = securities;

  const isAssetClassStrategy =
    draftModel.strategy === FinancialProductStrategy.AssetClass;
  const assetClassLevel = draftModel.assetClassLevel;

  const stableAssetClassBranches = useMemo(
    () => draftModel.assetClassBranches ?? [],
    [draftModel.assetClassBranches],
  );

  const { hasCashBranch, cashLoading, cashProduct, cashError } =
    useModelPortfolioCash(stableAssetClassBranches, assetClassLevel);

  useEffect(() => {
    if (cashError) {
      enqueueToast(t('cashFetchError'), { variant: 'error' });
    }
  }, [cashError, enqueueToast, t]);

  const updateSecuritiesState = useCallback((nextSecurities: Security[]) => {
    setSecurities(nextSecurities);
  }, []);

  const onCsvImportComplete = useCallback(
    (imported: Security[]) => {
      updateSecuritiesState(
        imported.map((row) => normalizeCashSecurity(row, cashProduct)),
      );
    },
    [updateSecuritiesState, cashProduct],
  );

  const {
    importLoading,
    showReviewDialog,
    unrecognizedSecurities,
    pendingImportData,
    handleImportFromCsv,
    handleReviewSubmit: handleReviewSubmitFromHook,
    handleReviewClose,
  } = useCsvSecuritiesImport({
    existingSecurities: securities,
    onImportComplete: onCsvImportComplete,
    isAssetClassStrategy,
  });

  const reportToWorkflow = useRef(setWorkflowData);
  reportToWorkflow.current = setWorkflowData;

  const persistSecurities = useCallback((rows: Security[]) => {
    void reportToWorkflow.current((previousData) => ({
      ...previousData,
      modelPortfolioDetails: {
        ...(previousData.modelPortfolioDetails as EditedHouseholdModelPortfolio),
        securities: rows,
      },
    }));
  }, []);

  /** The rows context already holds, so an unchanged draft costs nothing. */
  const persistedSecurities = useRef(draftModel.securities);
  /** Rows waiting on the debounce, kept so they can be flushed early. */
  const pendingSecurities = useRef<Security[] | undefined>(undefined);

  const flushPendingSecurities = useCallback(() => {
    const pending = pendingSecurities.current ?? securitiesRef.current;
    if (!pending || pending === persistedSecurities.current) return;
    pendingSecurities.current = undefined;
    persistedSecurities.current = pending;
    persistSecurities(pending);
  }, [persistSecurities]);

  /**
   * Writing the rows to the workflow context is a server round trip — it saves
   * the whole context, with retries — and the table reports every keystroke in
   * a percentage field. So they are coalesced rather than sent one per
   * character, and the first render, which only mirrors what the draft already
   * carried, is skipped outright.
   */
  useEffect(() => {
    if (securities === persistedSecurities.current) return;

    pendingSecurities.current = securities;
    const timer = setTimeout(flushPendingSecurities, PERSIST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [securities, flushPendingSecurities]);

  // The allocations tab is unmounted when the advisor moves to the IPS tab, so
  // anything still waiting on the debounce has to be written before it goes.
  useEffect(() => () => flushPendingSecurities(), [flushPendingSecurities]);

  // Validation and commit read the workflow context, but the table's rows live
  // here until the debounce lands. Mirror them up synchronously so an Enter
  // submit from a weight field cannot skip the advisor's last edit.
  useEffect(() => {
    liveSecuritiesContext?.setLiveSecurities(securities);
  }, [liveSecuritiesContext, securities]);

  useEffect(() => {
    liveSecuritiesContext?.registerFlushPendingSecurities(
      flushPendingSecurities,
    );
    return () => liveSecuritiesContext?.registerFlushPendingSecurities(null);
  }, [flushPendingSecurities, liveSecuritiesContext]);

  const handleAddSecurities = useCallback(
    (products: FinancialProduct[]) => {
      updateSecuritiesState(appendSecurityRows(securities, products));
    },
    [securities, updateSecuritiesState],
  );

  const isCashSecurity = useCallback(
    (row: Security) =>
      row.financialProduct.isCash ||
      (cashProduct != null && row.financialProduct.id === cashProduct.id),
    [cashProduct],
  );

  const normalizedForCashIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!cashProduct) return;
    if (normalizedForCashIdRef.current === cashProduct.id) return;
    const hasCashSecurity = securitiesRef.current.some(
      (row) =>
        row.financialProduct.isCash ||
        row.financialProduct.id === cashProduct.id,
    );
    if (!hasCashSecurity) return;
    normalizedForCashIdRef.current = cashProduct.id;
    updateSecuritiesState(
      securitiesRef.current.map((row) =>
        normalizeCashSecurity(row, cashProduct),
      ),
    );
  }, [cashProduct, updateSecuritiesState]);

  const handleAddCash = useCallback(() => {
    if (cashLoading) return;
    if (securities.some((row) => row.financialProduct.isCash)) return;

    if (!cashProduct) {
      enqueueToast(t('buildModels:noCashAssetClassFound'), {
        variant: 'error',
      });
      return;
    }

    const newCashSecurity: Security = {
      financialProduct: cashProduct,
      percentage: 0,
      isCashEquivalent: false,
      taxRank: undefined,
      id: `${TEMP_ID_PREFIX}cash-${cashProduct.id}`,
    };

    updateSecuritiesState([...securities, newCashSecurity]);
  }, [
    cashLoading,
    cashProduct,
    enqueueToast,
    securities,
    t,
    updateSecuritiesState,
  ]);

  const handleUpdateSecurity = useCallback(
    (index: number, security: Security) => {
      const updated = [...securities];
      const current = updated[index];
      let next = { ...security };

      if (
        current.financialProduct.id?.startsWith(TEMP_ID_PREFIX) &&
        !security.financialProduct.id
      ) {
        next.financialProduct.id = current.financialProduct.id;
        next.id = current.financialProduct.id;
      }

      next = normalizeCashSecurity(next, cashProduct);
      updated[index] = next;
      updateSecuritiesState(updated);
    },
    [cashProduct, securities, updateSecuritiesState],
  );

  const handleRemoveSecurity = useCallback(
    (index: number) => {
      const removedTaxRank = getEffectiveTaxRankAtIndex(
        securities,
        index,
        isCashSecurity,
      );
      const filtered = securities.filter((_, rowIndex) => rowIndex !== index);
      const compacted = compactTaxRanksAfterRemoval(
        filtered,
        isCashSecurity,
        removedTaxRank,
      ).map((row) => normalizeCashSecurity(row, cashProduct));
      updateSecuritiesState(compacted);
    },
    [cashProduct, isCashSecurity, securities, updateSecuritiesState],
  );

  const handleUpdateTaxRank = useCallback(
    (index: number, value: number) => {
      const updated = [...securities];
      updated[index] = { ...updated[index], taxRank: Number(value) };
      updateSecuritiesState(updated);
    },
    [securities, updateSecuritiesState],
  );

  const handleUpdateTargetPercentage = useCallback(
    (index: number, value: number) => {
      const updated = [...securities];
      updated[index] = { ...updated[index], percentage: value };
      updateSecuritiesState(updated);
    },
    [securities, updateSecuritiesState],
  );

  const handleReorderSecurities = useCallback(
    (reordered: Security[]) => {
      const cashRows = reordered.filter(isCashSecurity);
      const nonCashRows = reordered.filter((row) => !isCashSecurity(row));

      const updated = [...nonCashRows, ...cashRows].map((row, index) => {
        const normalized = normalizeCashSecurity(row, cashProduct);
        return {
          ...normalized,
          financialProduct: { ...normalized.financialProduct },
          id: normalized.id || normalized.financialProduct.id,
          taxRank: isCashSecurity(row) ? undefined : (row.taxRank ?? index + 1),
        };
      });

      updateSecuritiesState(updated);
    },
    [cashProduct, isCashSecurity, updateSecuritiesState],
  );

  const handleReviewSubmit = useCallback(
    async (replacements: Record<string, string>) => {
      try {
        await handleReviewSubmitFromHook(replacements);
      } catch (error) {
        enqueueToast(error instanceof Error ? error.message : String(error), {
          variant: 'error',
        });
      }
    },
    [enqueueToast, handleReviewSubmitFromHook],
  );

  const { securitiesWithRanks } = useMemo(
    () => getTaxRankValidation(securities, isAssetClassStrategy),
    [securities, isAssetClassStrategy],
  );

  const { groupedSecurities, unassignedSecurities } = useAssetClassGrouping(
    isAssetClassStrategy,
    securitiesWithRanks,
    stableAssetClassBranches,
    assetClassLevel,
  );

  return {
    securities,
    securitiesWithRanks,
    isAssetClassStrategy,
    assetClassLevel,
    groupedSecurities,
    unassignedSecurities,
    flushPendingSecurities,
    onAddSecurities: handleAddSecurities,
    onAddCash:
      isAssetClassStrategy && (hasCashBranch || cashLoading)
        ? handleAddCash
        : undefined,
    onUpdateSecurity: handleUpdateSecurity,
    onRemoveSecurity: handleRemoveSecurity,
    onUpdateTaxRank: handleUpdateTaxRank,
    onUpdateTargetPercentage: handleUpdateTargetPercentage,
    onReorderSecurities: handleReorderSecurities,
    importLoading,
    showReviewDialog,
    unrecognizedSecurities,
    availableSecurities: (pendingImportData?.availableSecurities ??
      []) as AvailableSecurity[],
    handleImportFromCsv,
    handleReviewSubmit,
    handleReviewClose,
    cashLoading,
  };
};
